import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useShiftRotationApi from './config';
import { createShiftRotation } from '../../services/shiftRotationService';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

export default function ShiftRotationsPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { canWrite } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, ids: [], name: '' });
  const [deleting, setDeleting] = useState(false);

  const { objectSchema, fetching, columns: baseColumns, data, pagination, _delete } = useShiftRotationApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(async (formData) => {
    try {
      await createShiftRotation(formData);
      setDrawerOpen(false);
      setQueryParams((prev) => ({ ...prev }));
      return true;
    } catch {
      return false;
    }
  }, []);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, ids: [row.id], name: row.name });
  }, []);

  const handleRowsDelete = useCallback((ids) => {
    setDeleteConfirm({ isOpen: true, ids, name: `${ids.length} ${t('shiftRotation.title').toLowerCase()}` });
  }, [t]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await _delete(deleteConfirm.ids);
    setDeleteConfirm({ isOpen: false, ids: [], name: '' });
    setDeleting(false);
  }, [_delete, deleteConfirm.ids]);

  const handleRowClick = useCallback((row) => {
    history.push(`/shift-rotations/${row.id}`);
  }, [history]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useMemo(() => {
    if (!canWrite('SM')) return [];
    return [
      { type: 'details', label: t('shiftRotation.details'), onClick: (row) => history.push(`/shift-rotations/${row.id}`) },
      { type: 'divider' },
      { type: 'delete', label: t('shiftRotation.delete'), onClick: handleDelete },
    ];
  }, [t, canWrite, handleDelete, history]);

  const columns = useMemo(() => {
    if (actionOptions.length === 0) return baseColumns;
    return [
      ...baseColumns,
      {
        name: 'actions',
        label: ' ',
        options: {
          filter: false, sort: false, download: false, print: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            return <OptionsMenu actions={actionOptions} row={row} />;
          },
        },
      },
    ];
  }, [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={t('shiftRotation.new')}
        schema={objectSchema}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('shiftRotation.deleteTitle')}
        message={t('shiftRotation.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, ids: [], name: '' })}
        loading={deleting}
      />

      {useMemo(() => (
        <DataTable
          title={t('shiftRotation.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={canWrite('SM')}
          onNewButtonClick={handleNewButtonClick}
          onRowsDelete={canWrite('SM') ? handleRowsDelete : undefined}
          onRowClick={handleRowClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
        />
      ), [
        t, fetching, columns, data, pagination, canWrite,
        handleNewButtonClick, handleRowsDelete, handleRowClick,
        filterList, handleFilterConfirm, handleDataStateChange,
      ])}
    </Container>
  );
}
